import { useCallback, useState, useEffect } from 'react';
import './App.css';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Sidebar from './components/Sidebar/Sidebar';
import { useUser } from './components/UserProvider/UserProvider';
import { useLanguageService } from './translations';
import useNavigation, { getUrlLang } from './hooks/useNavigation';
import { prepareUrl } from 'helpers';
import integration from 'services/integration';
import auth from 'services/auth';
import useViewMode from './hooks/useViewMode';
import { isRoobetHost } from './hostsConfig';

function App() {
  const { lang, setLang, langCodes } = useLanguageService();
  const [currency, setCurrency] = useState();
  const {
    user,
    setUser,
    isAuthPending,
    setIsAuthPending,
    refreshBalance,
    setRefreshBalance,
    transferBalance,
    setTransferBalance,
  } = useUser();
  const { navigateByPath, clientPath, search } =
    useNavigation(lang, langCodes);
  const [url, setUrl] = useState(prepareUrl(lang, '/'));
  const iframeUrlLang = getUrlLang(url, langCodes);
  const { isWideView } = useViewMode();

  useEffect(() => {
    integration.init({
      languages: langCodes,
    });
  }, [langCodes]);

  const logout = useCallback(async () => {
    localStorage.removeItem('token');
    integration.helpers.logout();
    await auth.logout();
    setUser(null);
  }, [setUser]);

  const login = useCallback(() => {
    navigateByPath('/login');
  }, [navigateByPath]);

  const fetchTransferBalance = useCallback(async (payload, cb) => {
    await auth.transferBalance(payload, cb);
  }, []);

  useEffect(() => {
    if (!url && clientPath) {
      const urlLang = getUrlLang(clientPath, langCodes);
      urlLang && urlLang !== lang && setLang(urlLang);
    }
  }, [clientPath, lang, langCodes, setLang, url]);

  useEffect(() => {
    if (iframeUrlLang !== lang) {
      setLang(lang);
      setUrl(prepareUrl(lang, '/'));
    }
  }, [iframeUrlLang, lang, setLang]);

  useEffect(() => {
    integration.mount('frame', url);
  }, [url]);

  const checkUser = useCallback(async () => {
    if (localStorage.getItem('token')) {
      const user = await auth.getUser(localStorage.getItem('token'));
      setUser(user);
      integration.emit({ balance: user?.balance, currency: user?.currency });
    }
  }, [setUser]);

  useEffect(() => {
    if (user?.jwt) {
      setUrl((url) => {
        const data = new URL(url);
        const search = new URLSearchParams({
          jwt: user.jwt.jwt,
        });
        return data.origin + data.pathname + '?' + search.toString();
      });
      setIsAuthPending(false);
    }
  }, [setIsAuthPending, user?.jwt, lang, search, setUrl]);

  useEffect(() => {
    checkUser();
  }, [refreshBalance, checkUser]);

  useEffect(() => {
    const on = integration.on;
    const unsubscriptions = [];
    unsubscriptions.push(on('login.click', login));
    unsubscriptions.push(
      on('user.balance', () => {
        setRefreshBalance((v) => v + 1);
      }),
    );
    unsubscriptions.push(on('user.balance_transfer', setTransferBalance));
    return () => {
      unsubscriptions.forEach(({ off }) => {
        off();
      });
    };
  }, [login, setTransferBalance, setRefreshBalance]);

  useEffect(() => {
    if (transferBalance.value) {
      fetchTransferBalance(transferBalance.value, (val) => {
        integration.emit({ balanceTransferSuccess: val });
        checkUser();
      });
    }
  }, [transferBalance, fetchTransferBalance, checkUser]);

  const changeCurrency = (currency) => {
    integration.helpers.logout();
    setCurrency(currency);
  };

  const refreshCallback = () => {
    navigateByPath('/');
    window.location.reload();
  };

  if (!url || isAuthPending) {
    return null;
  }

  return (
    <div className={`App ${isRoobetHost ? 'roobet' : ''}`}>
      <Header
        currency={currency}
        user={user}
        login={login}
        logout={logout}
        refresh={refreshCallback}
        updateLang={setLang}
        changeCurrency={changeCurrency}
      />
      <main>
        {isRoobetHost && isWideView && <Sidebar />}
        <div id="frame"></div>
      </main>
      <Footer />
    </div>
  );
}

export default App;
